import { useLazyQuery } from '@apollo/client';
import { showToast } from 'components/common/CustomToast';
import AccountContext from 'context/AccountContext';
import { VERIFY_WALLET_TOKEN } from 'graphql/queries/verifyWalletToken';
import { useContext, useEffect } from 'react';
import { useWalletTokenVerifyStore } from 'stores/walletTokenVerifyStore';
import { Query } from 'types/generatedSchemaTypes';
import { isWeb } from 'utils/deviceUtils';

export const useVerifyWalletToken = () => {
  const { walletTokenPendingVerification, setWalletTokenPendingVerification } =
    useWalletTokenVerifyStore((state) => state);
  const { customer, refetchCustomer } = useContext(AccountContext);

  const [verifyWalletToken] = useLazyQuery<Pick<Query, 'verifyWalletToken'>>(
    VERIFY_WALLET_TOKEN,
    {
      fetchPolicy: 'network-only',
      onCompleted: async (data) => {
        const result = data?.verifyWalletToken;
        if (result?.status === 'ok') {
          showToast('success.walletTokenVerified', 'success');
          const refetchWithRetry = async (attempts: number) => {
            const refetchResult = await refetchCustomer();
            const hasMobilePay =
              refetchResult?.data?.readAppCustomer?.paymentOptions?.some(
                (option) => option?.method === 'MOBILEPAY'
              );
            if (hasMobilePay) {
              setWalletTokenPendingVerification(null);
            } else if (attempts > 0) {
              setTimeout(() => refetchWithRetry(attempts - 1), 1000);
            } else {
              setWalletTokenPendingVerification(null);
            }
          };
          refetchWithRetry(3);
        } else {
          showToast('error.mobilePayVerification', 'error');
          setWalletTokenPendingVerification(null);
        }
      },
      onError: () => {
        showToast('error.mobilePayVerification', 'error');
        setWalletTokenPendingVerification(null);
      },
    }
  );

  useEffect(() => {
    if (!walletTokenPendingVerification) {
      return;
    }
    const { paymentMethod, userId } = walletTokenPendingVerification;
    if (!customer?.uid) {
      return;
    }
    if (paymentMethod && userId === customer.uid) {
      verifyWalletToken({
        variables: {
          input: {
            paymentMethod,
            paymentProvider: 'AIMO_ONE_FI',
          },
        },
      });
      if (isWeb && window.location.pathname.includes('MOBILEPAY')) {
        window.history.replaceState({}, '', '/account/my-payment-methods');
      }
      return;
    }
    setWalletTokenPendingVerification(null);
  }, [
    walletTokenPendingVerification,
    customer?.uid,
    verifyWalletToken,
    setWalletTokenPendingVerification,
  ]);
};
